/**
 * AdminSidebar Component
 * Reorganized with accordion menu groups for better navigation
 * Includes permission checking for restricted admin access
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Image as ImageIcon, Settings, Mail, Users,
  Megaphone, Gift, Mic, Tag, UserPlus, Link2, Layout, Send,
  ChevronDown, ChevronRight, Globe, Podcast, Home, Sparkles,
  LogOut, ArrowLeft, Lock
} from 'lucide-react';

// Permission mapping: tab ID -> {category, feature}
const TAB_PERMISSION_MAP = {
  // Content
  'pages': { category: 'content', feature: 'pages' },
  'media': { category: 'content', feature: 'media_library' },
  'homepage-layout': { category: 'content', feature: 'page_layout' },
  // Podcast
  'episodes': { category: 'podcast', feature: 'episodes' },
  'series': { category: 'podcast', feature: 'series' },
  'guests': { category: 'podcast', feature: 'guests' },
  // Membership (hosts is part of podcast but mapped to members for simplicity)
  'clubhouse': { category: 'membership', feature: 'members' },
  'member-locker': { category: 'membership', feature: 'perks' },
  'modals': { category: 'membership', feature: 'member_modals' },
  'giveaways': { category: 'marketing', feature: 'giveaways' },
  // Marketing
  'members': { category: 'marketing', feature: 'newsletter' },
  'email-templates': { category: 'marketing', feature: 'newsletter' },
  'email-studio': { category: 'marketing', feature: 'newsletter' },
  'linkhub': { category: 'marketing', feature: 'sponsors' },
  // Administration - always requires super_admin
  'settings': { category: 'admin', feature: 'settings' },
  'team': { category: 'admin', feature: 'team' },
};

// Grouped navigation structure
const NAV_GROUPS = [
  {
    id: 'content',
    label: 'Content',
    icon: Globe,
    defaultOpen: true,
    items: [
      { id: 'pages', label: 'Pages', icon: FileText },
      { id: 'media', label: 'Media Library', icon: ImageIcon },
      { id: 'homepage-layout', label: 'Page Layout', icon: Layout },
    ]
  },
  {
    id: 'podcast',
    label: 'Podcast',
    icon: Podcast,
    defaultOpen: false,
    items: [
      { id: 'episodes', label: 'Episodes', icon: Mic },
      { id: 'series', label: 'Series', icon: Tag },
      { id: 'guests', label: 'Guests', icon: UserPlus },
    ]
  },
  {
    id: 'membership',
    label: 'Membership',
    icon: Users,
    defaultOpen: false,
    items: [
      { id: 'clubhouse', label: 'Clubhouse', icon: Home },
      { id: 'member-locker', label: 'Member Vault', icon: Gift },
      { id: 'modals', label: 'Member Modals', icon: Megaphone },
      { id: 'giveaways', label: 'Giveaways', icon: Sparkles },
    ]
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Send,
    defaultOpen: false,
    items: [
      { id: 'members', label: 'Newsletter', icon: Mail },
      { id: 'email-templates', label: 'Welcome Email', icon: Mail },
      { id: 'email-studio', label: 'Email Campaigns', icon: Send },
      { id: 'linkhub', label: 'Link Hub', icon: Link2 },
    ]
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: Settings,
    defaultOpen: false,
    items: [
      { id: 'settings', label: 'Site Settings', icon: Settings },
      { id: 'team', label: 'Admin Team', icon: Users },
    ]
  }
];

// Tab title mapping
export const TAB_TITLES = {
  'pages': 'Page Management',
  'media': 'Media Library',
  'settings': 'Site Settings',
  'members': 'Newsletter Signups',
  'clubhouse': 'Clubhouse Management',
  'modals': 'Member Modals',
  'member-locker': 'Member Vault (Perks)',
  'team': 'Admin Team Management',
  'episodes': 'Episode Management',
  'series': 'Series Management',
  'guests': 'Guest Management',
  'linkhub': 'Link Hub Pages',
  'giveaways': 'Giveaway Management',
  'homepage-layout': 'Home Page Layout',
  'email-templates': 'Welcome Email',
  'email-studio': 'Email Studio',
};

// Find which group contains the active tab
const findActiveGroup = (activeTab) => {
  for (const group of NAV_GROUPS) {
    if (group.items.some(item => item.id === activeTab)) {
      return group.id;
    }
  }
  return null;
};

// Check if user has permission for a tab
const hasTabPermission = (tabId, adminUser) => {
  // Super admins always have access
  if (adminUser?.role === 'super_admin') return { hasAccess: true, level: 'write' };
  
  const permMap = TAB_PERMISSION_MAP[tabId];
  if (!permMap) return { hasAccess: true, level: 'write' }; // Unknown tabs are allowed
  
  // Administration tabs require super_admin
  if (permMap.category === 'admin') {
    return { hasAccess: adminUser?.role === 'super_admin', level: adminUser?.role === 'super_admin' ? 'write' : 'none' };
  }
  
  const permissions = adminUser?.permissions || {};
  const categoryPerms = permissions[permMap.category] || {};
  const level = categoryPerms[permMap.feature] || 'none';
  
  return { 
    hasAccess: level !== 'none', 
    level 
  };
};

// Export for use in Admin.jsx
export { TAB_PERMISSION_MAP, hasTabPermission };

// Nav Group Component with accordion behavior
const NavGroup = ({ group, activeTab, onTabChange, isOpen, onToggle, adminUser, onAccessDenied }) => {
  const GroupIcon = group.icon;
  const hasActiveItem = group.items.some(item => item.id === activeTab);
  
  const handleItemClick = (tabId) => {
    const { hasAccess, level } = hasTabPermission(tabId, adminUser);
    if (!hasAccess) {
      onAccessDenied(tabId, TAB_TITLES[tabId] || tabId);
    } else {
      onTabChange(tabId, level);
    }
  };
  
  return (
    <div className={`nav-group ${isOpen ? 'open' : ''} ${hasActiveItem ? 'has-active' : ''}`}>
      <button 
        className="nav-group-header"
        onClick={onToggle}
        data-testid={`nav-group-${group.id}`}
      >
        <GroupIcon size={18} className="group-icon" />
        <span className="group-label">{group.label}</span>
        <span className="group-chevron">
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        {hasActiveItem && !isOpen && <span className="active-indicator" />}
      </button>
      
      <div className={`nav-group-items ${isOpen ? 'expanded' : 'collapsed'}`}>
        {group.items.map(({ id, label, icon: Icon }) => {
          const { hasAccess, level } = hasTabPermission(id, adminUser);
          const isRestricted = !hasAccess;
          
          return (
            <button
              key={id}
              className={`nav-item ${activeTab === id ? 'active' : ''} ${isRestricted ? 'restricted' : ''} ${level === 'read' ? 'read-only' : ''}`}
              onClick={() => handleItemClick(id)}
              data-testid={`nav-item-${id}`}
              title={isRestricted ? 'Access Denied' : (level === 'read' ? 'Read Only' : '')}
            >
              <Icon size={16} />
              <span>{label}</span>
              {isRestricted && <Lock size={12} className="lock-icon" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const AdminSidebar = ({ 
  activeTab, 
  onTabChange, 
  sidebarOpen,
  onLogout,
  adminUser,
  onAccessDenied
}) => {
  const navigate = useNavigate();
  
  // Initialize open groups - open the group containing active tab
  const [openGroups, setOpenGroups] = useState(() => {
    const initialOpen = {};
    NAV_GROUPS.forEach(group => {
      // Open if it's the default or contains active tab
      initialOpen[group.id] = group.defaultOpen || 
        group.items.some(item => item.id === activeTab);
    });
    return initialOpen;
  });

  const handleToggleGroup = (groupId) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleTabChange = (tabId, permissionLevel) => {
    // Auto-expand the group containing the new tab
    const activeGroup = findActiveGroup(tabId);
    if (activeGroup && !openGroups[activeGroup]) {
      setOpenGroups(prev => ({
        ...prev,
        [activeGroup]: true
      }));
    }
    onTabChange(tabId, permissionLevel);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('golfnuts_admin_token');
    sessionStorage.removeItem('golfnuts_admin_auth');
    sessionStorage.removeItem('golfnuts_admin_user');
    navigate('/admin/login');
    if (onLogout) onLogout();
  };

  return (
    <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
      <div className="admin-logo">
        <h1>Golf Nuts</h1>
        <p>Admin Panel</p>
      </div>
      
      <nav className="admin-nav accordion-nav">
        {NAV_GROUPS.map(group => (
          <NavGroup
            key={group.id}
            group={group}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isOpen={openGroups[group.id]}
            onToggle={() => handleToggleGroup(group.id)}
            adminUser={adminUser}
            onAccessDenied={onAccessDenied}
          />
        ))}
      </nav>
      
      <div className="admin-sidebar-footer">
        <a href="/" className="btn-back-to-site">
          <ArrowLeft size={16} />
          Back to Site
        </a>
        <button className="btn-logout" onClick={handleLogout}>
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
